import React from "react"
import clsx from "clsx"
import dayjs from "dayjs"
import { Upload, Button, message } from "antd"
import { UploadOutlined, LeftOutlined, LinkOutlined } from "@ant-design/icons"
import type { UploadFile } from "antd/es/upload/interface"
import type { AttachmentItem, ReviewMilestone } from "../types"
import { uploadFileApi } from "@/services/apis/file"

/** 金额格式化（万元） */
export const formatMoney = (v?: number) => {
    if (v === undefined || v === null || Number.isNaN(v)) return "-"
    const sign = v < 0 ? "-" : ""
    return `${sign}${Math.abs(v).toLocaleString("zh-CN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`
}

export const formatPercent = (v?: number) => {
    if (v === undefined || v === null || Number.isNaN(v)) return "-"
    return `${v.toFixed(2)}%`
}

/** 工时数量格式化（保留 1 位小数） */
export const formatHours = (v?: number) => {
    if (v === undefined || v === null || Number.isNaN(v)) return "-"
    return Number(v).toFixed(1)
}

/** 金额展示，负数标红 */
export const Money: React.FC<{ value?: number; unit?: string; className?: string }> = ({
    value,
    unit = "万",
    className,
}) => (
    <span className={clsx(value !== undefined && value < 0 ? "text-red-500" : "text-gray-800", className)}>
        {formatMoney(value)}
        {value !== undefined && value !== null ? unit : ""}
    </span>
)

export const Percent: React.FC<{ value?: number; className?: string }> = ({ value, className }) => (
    <span className={clsx(value !== undefined && value < 0 ? "text-red-500" : "text-gray-800", className)}>
        {formatPercent(value)}
    </span>
)

/** 页面标题栏（带返回） */
export const PageHeader: React.FC<{
    title: React.ReactNode
    subtitle?: React.ReactNode
    onBack?: () => void
    extra?: React.ReactNode
}> = ({ title, subtitle, onBack, extra }) => (
    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-gray-200 px-5 py-3 flex items-center gap-3">
        {onBack && (
            <button
                onClick={onBack}
                className="shrink-0 flex items-center gap-1 text-sm text-gray-500 hover:text-dark-bg transition-colors"
            >
                <LeftOutlined /> 返回
            </button>
        )}
        <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-gray-800 truncate">{title}</h2>
            {subtitle && <p className="text-xs text-gray-400 mt-0.5 truncate">{subtitle}</p>}
        </div>
        {extra}
    </div>
)

/**
 * 回填提示条：再次打开表单、读取到当前复盘自己的保存内容（暂存 / 提交）并回填后展示。
 * 无保存内容时不展示；提示文案含「暂存」/「已提交」以便区分回填来源。
 */
export const RestoreBanner: React.FC<{ status: "draft" | "submitted"; submittedAt?: string }> = ({
    status,
    submittedAt,
}) => {
    const label = status === "submitted" ? "已提交" : "暂存"
    const time = submittedAt ? `（${dayjs(submittedAt).format("YYYY-MM-DD HH:mm")}）` : ""
    return (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            <span className="shrink-0 rounded bg-emerald-500/90 px-1.5 py-0.5 text-xs font-medium text-white">
                回填
            </span>
            <span className="ml-2">已回填{label}内容{time}，可在已保存内容基础上继续编辑后再次暂存 / 提交。</span>
        </div>
    )
}

/** 卡片分区 */
export const SectionCard: React.FC<{
    title?: React.ReactNode
    desc?: React.ReactNode
    required?: boolean
    extra?: React.ReactNode
    className?: string
    bodyClassName?: string
    children: React.ReactNode
}> = ({ title, desc, required, extra, className, bodyClassName, children }) => (
    <section className={clsx("bg-white rounded-xl border border-gray-200 shadow-sm", className)}>
        {(title || extra) && (
            <header className="flex items-start justify-between gap-3 px-5 py-3 border-b border-gray-100">
                <div className="flex items-start gap-2">
                    <span className="mt-1 h-4 w-1 rounded-full bg-dark-bg" />
                    <div>
                        <h3 className="text-[15px] leading-6 font-semibold text-gray-800">
                            {title}
                            {required && <span className="text-red-500 ml-0.5">*</span>}
                        </h3>
                        {desc && <p className="text-xs text-gray-400 mt-0.5">{desc}</p>}
                    </div>
                </div>
                {extra}
            </header>
        )}
        <div className={clsx("p-5", bodyClassName)}>{children}</div>
    </section>
)

/** 信息项（标签 + 值） */
export const InfoItem: React.FC<{
    label: React.ReactNode
    children: React.ReactNode
    className?: string
}> = ({ label, children, className }) => (
    <div className={clsx("min-w-0", className)}>
        <div className="text-xs text-gray-400 mb-1">{label}</div>
        <div className="text-sm text-gray-800 font-medium break-words">{children}</div>
    </div>
)

/** 必填字段标签 */
export const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean; className?: string }> = ({
    children,
    required,
    className,
}) => (
    <div className={clsx("text-sm text-gray-700 mb-1.5", className)}>
        {children}
        {required && <span className="text-red-500 ml-0.5">*</span>}
    </div>
)

/** 外部链接 */
export const ExternalLink: React.FC<{ href?: string; className?: string; children: React.ReactNode }> = ({
    href,
    className,
    children,
}) => (
    <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className={clsx(
            "inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 hover:underline",
            className
        )}
    >
        <LinkOutlined />
        {children}
    </a>
)

/** 只读文本：空值展示 - */
export const text = (v?: string): string => (v && v.trim() ? v : "-")

/** 只读附件列表（文件名 + 下载链接；无附件时不渲染） */
export const AttachmentList: React.FC<{ attachments?: AttachmentItem[] }> = ({ attachments }) => {
    if (!attachments || attachments.length === 0) return null
    return (
        <ul className="space-y-1.5">
            {attachments.map((a) => (
                <li key={a.uid} className="text-sm">
                    {a.url ? (
                        <a
                            href={a.url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:text-blue-700 hover:underline"
                        >
                            {a.name}
                        </a>
                    ) : (
                        <span className="text-gray-700">{a.name}</span>
                    )}
                </li>
            ))}
        </ul>
    )
}

/** A2 里程碑只读列表（里程碑名称 / 计划时间 / 实际时间 / 甲方确认单 / 提前开票 / 状态，与里程碑页「节点时间与状态设置」同结构；表单与只读预览共用） */
export const MilestoneTable: React.FC<{ list: ReviewMilestone[] }> = ({ list }) => (
    <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
            <thead>
                <tr className="text-left text-xs text-gray-400 bg-gray-50">
                    {["里程碑名称", "计划时间", "实际时间", "甲方确认单", "提前开票", "状态"].map((h) => (
                        <th key={h} className="font-normal px-3 py-2 whitespace-nowrap">
                            {h}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {list.map((m) => (
                    <tr key={m.milestoneCode} className="border-t border-gray-100">
                        <td className="px-3 py-2 text-gray-700">{text(m.milestoneName)}</td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{text(m.planDate)}</td>
                        <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{text(m.actualDate)}</td>
                        <td className="px-3 py-2">
                            {m.confirmations && m.confirmations.length > 0 ? (
                                <AttachmentList attachments={m.confirmations} />
                            ) : (
                                <span className="text-gray-500">-</span>
                            )}
                        </td>
                        <td className="px-3 py-2 text-gray-700">{m.isAdvanceInvoice ? "是" : "-"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                            {m.status === 1 ? (
                                <span className="text-green-500">已完成</span>
                            ) : m.status === 2 ? (
                                <span className="text-red-500">已延期</span>
                            ) : (
                                <span className="text-gray-800">未完成</span>
                            )}
                        </td>
                    </tr>
                ))}
                {list.length === 0 && (
                    <tr>
                        <td colSpan={6} className="px-3 py-6 text-center text-gray-400">
                            暂无里程碑数据
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
)

/** 附件上传（调用 POST /workbenchApi/uploads 真实上传，成功后回传文件 url） */
export const FileUploader: React.FC<{
    value: AttachmentItem[]
    onChange: (v: AttachmentItem[]) => void
    accept?: string
}> = ({ value, onChange, accept = ".pdf,.xlsx,.xls,.jpg,.jpeg,.png" }) => {
    const fileList: (UploadFile & { edocFileID?: number })[] = value.map((f) => ({
        uid: f.uid,
        name: f.name,
        status: "done",
        url: f.url,
        // Keep the DDS identifier attached to the UploadFile while the user
        // adds/removes files, so a later draft/submit does not lose it.
        edocFileID: f.edocFileID,
    }))

    return (
        <Upload
            accept={accept}
            fileList={fileList}
            customRequest={async ({ file, onSuccess, onError }) => {
                try {
                    const res = await uploadFileApi(file as File)
                    onSuccess?.(res)
                } catch (err) {
                    onError?.(err instanceof Error ? err : new Error(String(err)))
                }
            }}
            onChange={({ file, fileList: nextList }) => {
                if (file.status === "error") {
                    message.error(`${file.name} 上传失败`)
                }
                onChange(
                    nextList
                        .filter((f) => f.status !== "error")
                        .map((f) => ({
                            uid: f.uid,
                            name: f.name,
                            url:
                                f.url ??
                                (f.response as { file?: API.UploadFile } | undefined)?.file?.url,
                            edocFileID:
                                (f as UploadFile & { edocFileID?: number }).edocFileID ??
                                (f.response as { file?: { edocFileID?: number } } | undefined)?.file
                                    ?.edocFileID,
                        }))
                )
            }}
        >
            <Button icon={<UploadOutlined />}>选择文件</Button>
            <span className="ml-3 text-xs text-gray-400">支持 pdf / xlsx / jpg，可上传多个</span>
        </Upload>
    )
}

/** 底部操作栏 */
export const FormFooter: React.FC<{
    submitter?: React.ReactNode
    date?: string
    submitting?: boolean
    savingDraft?: boolean
    onSaveDraft?: () => void
    onSubmit?: () => void
}> = ({ submitter, date, submitting, savingDraft, onSaveDraft, onSubmit }) => (
    <div className="sticky bottom-0 bg-white/95 backdrop-blur border-t border-gray-200 px-5 py-3 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap items-center gap-6 text-sm text-gray-600">
            {submitter !== undefined && (
                <span>
                    填报人（管理所长）：<span className="text-gray-800 font-medium">{submitter || "-"}</span>
                </span>
            )}
            {date && (
                <span>
                    提交日期：<span className="text-gray-800 font-medium">{date}</span>
                </span>
            )}
        </div>
        <div className="ml-auto flex items-center gap-3">
            {onSaveDraft && (
                <Button loading={savingDraft} onClick={onSaveDraft}>
                    暂存
                </Button>
            )}
            {onSubmit && (
                <Button type="primary" loading={submitting} onClick={onSubmit}>
                    提交
                </Button>
            )}
        </div>
    </div>
)
